import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Save } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { useCompanySettings, useUpdateCompanySettings } from '@/entities/company';

const DEFAULT_CONCURRENCY = 20

export function OrganizationSettingsPage() {
  const { data: settings, isLoading } = useCompanySettings()
  const updateSettings = useUpdateCompanySettings()

  const [maxConcurrent, setMaxConcurrent] = useState<number>(DEFAULT_CONCURRENCY)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (settings && !loaded) {
      setMaxConcurrent(settings.max_concurrent_conversations)
      setLoaded(true)
    }
  }, [settings, loaded])

  const isDirty = loaded && maxConcurrent !== settings?.max_concurrent_conversations

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        max_concurrent_conversations: maxConcurrent,
      })
      toast.success('Settings saved')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save settings')
    }
  }

  if (isLoading) {
    return (
      <div className="w-full h-full p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-48" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full p-4 overflow-y-auto">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-heading font-bold mb-1">Organization Settings</h1>
          <p className="text-detail text-muted-foreground">
            Configure organization-wide settings
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={!isDirty || updateSettings.isPending}
        >
          <Save className="w-3 h-3" />
          {updateSettings.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <Card>
        <CardHeader className="border-b py-2">
          <CardTitle className="text-body">Concurrency Limit</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 pb-4">
          <div className="max-w-sm space-y-1">
            <label className="text-detail font-medium text-muted-foreground">
              Max Concurrent Conversations
            </label>
            <Input
              type="number"
              min="1"
              value={maxConcurrent}
              onChange={(e) => {
                const val = parseInt(e.target.value)
                setMaxConcurrent(val > 0 ? val : 1)
              }}
              className="h-8"
            />
            <p className="text-[11px] text-muted-foreground leading-tight">
              Default is {DEFAULT_CONCURRENCY}. Capacity above {DEFAULT_CONCURRENCY} concurrent conversations is not guaranteed and will be charged additionally.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}